import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, X, ChevronLeft, ChevronRight, RefreshCw, Loader2, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { saas, errMsg } from "@/lib/saasApi";
import { useI18n, usePageTitle } from "@/i18n";

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX = 20;

export default function NewProject() {
  const nav = useNavigate();
  const { t } = useI18n();
  usePageTitle("titles.newProject");
  const stages = t("np.stages");
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceIdx = useRef(null);
  const [stage, setStage] = useState(0);
  const [name, setName] = useState("");
  const [ratio, setRatio] = useState("portrait");
  const [photos, setPhotos] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [noCredits, setNoCredits] = useState(false);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const room = MAX - photos.length;
    const accepted = [];
    files.forEach((f) => {
      if (accepted.length >= room) return toast.error(t("np.toastMax", { max: MAX }));
      if (!ACCEPTED.includes(f.type)) return toast.error(t("np.toastType", { name: f.name }));
      if (f.size > 20 * 1024 * 1024) return toast.error(t("np.toastSize", { name: f.name }));
      accepted.push({ localId: crypto.randomUUID(), file: f, previewUrl: URL.createObjectURL(f) });
    });
    setPhotos((p) => [...p, ...accepted]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.localId !== id));
  const move = (i, to) => { if (to < 0 || to >= photos.length) return; const n = [...photos]; const [it] = n.splice(i, 1); n.splice(to, 0, it); setPhotos(n); };
  const doReplace = (i) => { replaceIdx.current = i; replaceRef.current?.click(); };
  const onReplaceFile = (e) => {
    const f = e.target.files?.[0]; const i = replaceIdx.current;
    if (f && i != null && ACCEPTED.includes(f.type) && f.size <= 20 * 1024 * 1024) {
      setPhotos((p) => p.map((x, idx) => idx === i ? { ...x, file: f, previewUrl: URL.createObjectURL(f) } : x));
    } else if (f) toast.error(t("np.toastInvalid"));
    if (replaceRef.current) replaceRef.current.value = "";
  };

  const runGeneration = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setNoCredits(false);
    setProgress(t("np.preparing"));
    let pid;
    try {
      pid = (await saas.createProject(name.trim(), ratio)).data.id;
    } catch (e) { setBusy(false); return toast.error(errMsg(e)); }
    try {
      await saas.reserve(pid);
    } catch (e) {
      setBusy(false);
      if (e?.response?.status === 402) { setNoCredits(true); return; }
      return toast.error(errMsg(e));
    }
    for (let i = 0; i < photos.length; i++) {
      setProgress(t("np.uploading", { i: i + 1, n: photos.length }));
      const form = new FormData();
      form.append("orderIndex", String(i));
      form.append("file", photos[i].file);
      try {
        await saas.uploadPhoto(pid, form);
      } catch (e) {
        await saas.release(pid).catch(() => {});
        setBusy(false);
        return toast.error(t("np.uploadFailed", { i: i + 1, msg: errMsg(e) }));
      }
    }
    setProgress(t("np.starting"));
    try {
      await saas.generate(pid);
      toast.success(t("np.started"));
      nav(`/projects/${pid}`, { replace: true });
    } catch (e) {
      setBusy(false);
      toast.error(errMsg(e));
    }
  };

  const canNext = stage === 0 ? !!name.trim() : stage === 1 ? photos.length >= 1 : true;

  return (
    <div data-testid="new-project">
      <div className="mb-8 flex items-center gap-2">
        {stages.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${i === stage ? "border-re-red bg-re-red text-white" : i < stage ? "border-re-red/30 bg-re-red/5 text-re-red" : "border-re-line bg-white text-re-muted"}`} data-testid={`stage-${i}`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/10 text-xs">{i + 1}</span> {s}
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-re-line bg-white p-6 md:p-8">
        {stage === 0 && (
          <div className="space-y-6" data-testid="stage-project">
            <div className="space-y-2">
              <Label htmlFor="pname">{t("np.nameLabel")}</Label>
              <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("np.namePlaceholder")} data-testid="project-name" className="border-re-line" maxLength={120} />
            </div>
            <div className="space-y-2">
              <Label>{t("np.formatLabel")}</Label>
              <div className="flex flex-col gap-3 sm:flex-row">
                {[{ k: "portrait", t: t("np.portrait"), d: t("np.portraitDesc") }, { k: "landscape", t: t("np.landscape"), d: t("np.landscapeDesc") }].map((r) => (
                  <button key={r.k} type="button" onClick={() => setRatio(r.k)} data-testid={`ratio-${r.k}`} className={`flex-1 rounded-2xl border p-4 text-left transition-colors ${ratio === r.k ? "border-re-red bg-re-red/5" : "border-re-line hover:border-re-red/30"}`}>
                    <p className="font-heading text-sm font-semibold text-re-charcoal">{r.t}</p>
                    <p className="text-xs text-re-muted">{r.d}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-re-muted">{t("np.ratioNote")}</p>
            </div>
          </div>
        )}

        {stage === 1 && (
          <div className="space-y-5" data-testid="stage-photos">
            <div>
              <p className="font-heading text-base font-semibold text-re-charcoal">{t("np.photosTitle")}</p>
              <p className="text-sm text-re-muted">{t("np.photosSub")}</p>
            </div>
            <button type="button" onClick={() => inputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }} data-testid="photo-drop" className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-re-line bg-re-soft py-10 hover:border-re-red/40">
              <Upload className="h-6 w-6 text-re-red" />
              <span className="text-sm font-medium text-re-charcoal">{t("np.dropText")}</span>
              <span className="text-xs text-re-muted">{t("np.dropHint", { max: MAX })}</span>
            </button>
            <input ref={inputRef} type="file" accept={ACCEPTED.join(",")} multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="photo-input" />
            <input ref={replaceRef} type="file" accept={ACCEPTED.join(",")} hidden onChange={onReplaceFile} data-testid="replace-input" />
            <p className="text-sm font-medium text-re-charcoal" data-testid="photo-count">{t("np.selected", { n: photos.length, max: MAX })}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((p, i) => (
                <div key={p.localId} className="overflow-hidden rounded-xl border border-re-line bg-white" data-testid={`photo-${i}`}>
                  <div className="relative aspect-square bg-re-soft">
                    <img src={p.previewUrl} alt={t("np.photoAlt", { n: i + 1 })} className="h-full w-full object-cover" />
                    <span className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white">{i + 1}</span>
                    <button type="button" onClick={() => removePhoto(p.localId)} data-testid={`photo-remove-${i}`} className="absolute right-1.5 top-1.5 rounded-full bg-black/60 p-1 text-white"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="flex items-center justify-between p-2">
                    <div className="flex gap-1">
                      <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)} data-testid={`photo-left-${i}`} className="rounded border border-re-line p-1 text-re-muted disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
                      <button type="button" disabled={i === photos.length - 1} onClick={() => move(i, i + 1)} data-testid={`photo-right-${i}`} className="rounded border border-re-line p-1 text-re-muted disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
                    </div>
                    <button type="button" onClick={() => doReplace(i)} data-testid={`photo-replace-${i}`} className="rounded border border-re-line p-1 text-re-muted" title={t("np.replaceTitle")}><RefreshCw className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stage === 2 && (
          <div className="space-y-5" data-testid="stage-generate">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-xl border border-re-line p-4"><p className="text-xs text-re-muted">{t("np.nameLabel")}</p><p className="truncate text-sm font-medium text-re-charcoal">{name}</p></div>
              <div className="rounded-xl border border-re-line p-4"><p className="text-xs text-re-muted">{t("np.formatLabel")}</p><p className="text-sm font-medium text-re-charcoal">{ratio === "portrait" ? t("np.portrait") : t("np.landscape")}</p></div>
              <div className="rounded-xl border border-re-line p-4"><p className="text-xs text-re-muted">{t("np.summaryPhotos")}</p><p className="text-sm font-medium text-re-charcoal">{photos.length}</p></div>
              <div className="rounded-xl border border-re-line p-4"><p className="text-xs text-re-muted">{t("np.summaryResult")}</p><p className="text-sm font-medium text-re-charcoal">{t("np.resultValue")}</p></div>
            </div>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
              {photos.map((p, i) => (<img key={p.localId} src={p.previewUrl} alt={t("np.photoAlt", { n: i + 1 })} className="aspect-square w-full rounded-lg object-cover" />))}
            </div>
            <p className="text-xs text-re-muted">{t("np.resultHint", { res: ratio === "portrait" ? "1080 × 1920" : "1920 × 1080" })}</p>
            {noCredits && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="no-credits">
                <p className="text-sm font-medium text-re-charcoal">{t("np.noCredits")}</p>
                <Button variant="outline" className="mt-3 rounded-full" data-testid="choose-package-btn" onClick={() => nav("/account/billing")}>{t("np.choosePackage")}</Button>
              </div>
            )}
            <Button onClick={() => setConfirmOpen(true)} disabled={busy || photos.length < 1} data-testid="generate-open" className="w-full rounded-full bg-re-red py-6 text-base font-semibold text-white hover:bg-re-deepred">
              {busy ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {progress}</> : <><Film className="mr-2 h-5 w-5" /> {t("np.generate")}</>}
            </Button>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-re-line pt-5">
          <Button variant="outline" onClick={() => (stage === 0 ? nav("/dashboard") : setStage((s) => s - 1))} disabled={busy} data-testid="np-back" className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> {stage === 0 ? t("common.cancel") : t("np.back")}</Button>
          {stage < 2 && <Button onClick={() => setStage((s) => s + 1)} disabled={!canNext} data-testid="np-next" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink disabled:opacity-40">{t("np.continueBtn")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-modal">
          <DialogHeader>
            <DialogTitle>{t("np.confirmTitle")}</DialogTitle>
            <DialogDescription>{t("np.confirmText")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel" className="rounded-full">{t("common.cancel")}</Button>
            <Button onClick={runGeneration} data-testid="confirm-generate" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{t("np.confirmGenerate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
